import { useRef, useState } from 'react'
import { Autocomplete } from '@react-google-maps/api'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { IconButton, Typography } from '@mui/material'
import { SearchIcon } from 'assets/icons'
import { GGBLogoDarkBg } from 'assets/icons/GGB-logo-dark-bg'
import { DashboardIcon } from 'assets/icons/dashboard'
import { HamburgerIcon } from 'assets/icons/hamburger'
import { VersionHistoryIcon } from 'assets/icons/version-history'
import { ResetIcon } from 'assets/icons/reset'
import { ClearIcon } from 'assets/icons/clear'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  DarkBlurBackground,
  HeadingElement,
  HeadingElementWrapper,
  LogoMenuToggle,
  MenuPopover,
  MenuToggle,
  PlacesInput,
} from 'components/gps-editor/CourseGpsEditorElements.styles'

interface Props {
  autoLoad: (e: any) => void
  courseName?: string
  clubName?: string
  onLocationSelected: () => void
  onOpenSidebar?: () => void
  onResetAll: () => void
  onClearMap: () => void
  placeChanged: () => void
  searchPlaceholder: string
}

export default function CourseGpsEditorHeader({
  autoLoad,
  courseName,
  clubName,
  onLocationSelected,
  onOpenSidebar,
  onResetAll,
  onClearMap,
  placeChanged,
  searchPlaceholder,
}: Props): JSX.Element {
  const intl = useIntl()
  const [headingMenuOpen, setHeadingMenuOpen] = useState<boolean>(false)
  const [placesSearchOpen, setPlacesSearchOpen] = useState<boolean>(false)
  const placesSearchInput = useRef<HTMLInputElement | null>(null)

  const togglePlacesSearch = () => {
    setPlacesSearchOpen((prev) => !prev)
    if (placesSearchOpen) {
      placesSearchInput.current?.focus()
    }
  }

  const handleHeadingMenuClose = () => {
    setHeadingMenuOpen(false)
  }

  const handleOpenSidebar = () => {
    onOpenSidebar?.()
    handleHeadingMenuClose()
  }

  const handleResetAll = () => {
    onResetAll()
    handleHeadingMenuClose()
  }

  const handleClearMap = () => {
    onClearMap()
    handleHeadingMenuClose()
  }

  const handleSearchPlaceChanged = () => {
    placeChanged()
    onLocationSelected()
  }

  return (
    <ClickAwayListener onClickAway={handleHeadingMenuClose}>
      <HeadingElementWrapper>
        <HeadingElement>
          <LogoMenuToggle onClick={() => setHeadingMenuOpen((prev) => !prev)}>
            <GGBLogoDarkBg width="29px" height="29px" className="logo" />
            <MenuToggle className="menu">
              <HamburgerIcon />
            </MenuToggle>
          </LogoMenuToggle>
          <div>
            <Typography fontSize={16} fontWeight={500} lineHeight={1} sx={{ marginBottom: '4px' }}>
              {clubName}
            </Typography>
            <Typography fontSize={12} color={'#BBBBBD'} lineHeight={1}>
              {courseName}
            </Typography>
          </div>
          <div>
            <IconButton color="primary" onClick={togglePlacesSearch}>
              <SearchIcon width="16px" height="16px" fill="#fff" />
            </IconButton>
          </div>
        </HeadingElement>
        {placesSearchOpen && (
          <DarkBlurBackground>
            <Autocomplete onLoad={autoLoad} onPlaceChanged={handleSearchPlaceChanged}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SearchIcon width="16px" height="16px" fill="#8A8A8E" />
                <PlacesInput
                  ref={placesSearchInput}
                  type="text"
                  placeholder={searchPlaceholder}
                  pattern=".{1,}"
                  required
                />
              </div>
            </Autocomplete>
          </DarkBlurBackground>
        )}
        {headingMenuOpen && (
          <MenuPopover>
            <Link
              to={'/'}
              style={{ color: '#fff', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '16px' }}
            >
              <DashboardIcon />
              <Typography fontSize={16} color={'white'}>
                Go to dashboard
              </Typography>
            </Link>
            {onOpenSidebar && (
              <div
                onClick={handleOpenSidebar}
                style={{
                  color: '#fff',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                }}
              >
                <VersionHistoryIcon />
                <Typography fontSize={16} color={'white'}>
                  Version history
                </Typography>
              </div>
            )}
            <div
              onClick={handleResetAll}
              style={{
                color: '#fff',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                cursor: 'pointer',
              }}
            >
              <ResetIcon />
              <Typography fontSize={16} color={'white'}>
                {intl.formatMessage({ id: 'strings.resetAll' })}
              </Typography>
            </div>
            <div
              onClick={handleClearMap}
              style={{
                color: '#fff',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                cursor: 'pointer',
              }}
            >
              <ClearIcon />
              <Typography fontSize={16} color={'white'}>
                {intl.formatMessage({ id: 'strings.clearMap' })}
              </Typography>
            </div>
          </MenuPopover>
        )}
      </HeadingElementWrapper>
    </ClickAwayListener>
  )
}
